import { getCutoffHolidays } from '../../backend/lambda/services/utils/cutoffHolidays';
import { DateTime, Duration } from 'luxon';
import {
  DateTimeInput,
  parseDateTime,
  parseInZone,
  parseTimeInput,
  toUtcISO,
} from 'utils/dateParsing';

const NOON_UTC_HOUR = 12;

/**
 * Adds minutes to a date-time in the site/default timezone and serializes the
 * result as UTC ISO for backend persistence.
 *
 * Use for claim/tariff service-end defaults and other persisted timestamp
 * offsets.
 */
export const addMinutesToDateTime = (
  baseDateTime: DateTimeInput,
  minutes: number,
  timezone?: string | null
) => {
  const parsed = parseInZone(baseDateTime, timezone);
  return parsed.isValid ? toUtcISO(parsed.plus({ minutes })) : '';
};

/**
 * Applies an `HH:mm` time input to a date while preserving the date's site
 * calendar day, then serializes the result as UTC ISO.
 *
 * Use when a time-only editor changes an existing service date-time.
 */
export const applyTimeToDateTime = (
  baseDateTime: DateTimeInput,
  timeValue: string,
  timezone?: string | null
) => {
  const parsedTime = parseTimeInput(timeValue);
  const baseDate = parseInZone(baseDateTime, timezone);

  if (!parsedTime.isValid || !baseDate.isValid) return '';

  return toUtcISO(
    baseDate.set({
      hour: parsedTime.hour,
      minute: parsedTime.minute,
      second: 0,
      millisecond: 0,
    })
  );
};

/**
 * Computes a positive duration between start/end, falling back to a default
 * duration when either side is invalid or non-positive.
 *
 * Use when UI edits should preserve an existing service duration but still have
 * a safe default.
 */
export const getDurationOrDefault = (
  start: DateTimeInput,
  end: DateTimeInput,
  defaultMinutes = 15
) => {
  const startDateTime = parseDateTime(start);
  const endDateTime = parseDateTime(end);
  const duration =
    startDateTime.isValid && endDateTime.isValid
      ? endDateTime.diff(startDateTime)
      : null;

  return duration && duration.as('milliseconds') > 0
    ? duration
    : Duration.fromObject({ minutes: defaultMinutes });
};

/**
 * Normalizes a payment date to noon UTC so date-only payments do not drift
 * across calendar days in downstream systems.
 *
 * Use for payment date persistence, not general timestamp normalization.
 */
export const normalizePaymentDate = (value: string | null) => {
  const parsed = value ? parseDateTime(value, { zone: 'utc' }) : DateTime.utc();
  const normalized = (parsed.isValid ? parsed : DateTime.utc())
    .startOf('day')
    .plus({ hours: NOON_UTC_HOUR });

  return normalized.toISO() || DateTime.utc().toISO();
};

/**
 * Moves a date backward until it lands on a weekday that is not a configured
 * holiday.
 *
 * Use for pay-period and payout calculations that cannot land on weekends or
 * holidays.
 */
export const rollBackToBusinessDay = (
  dateInput: DateTime,
  holidays: Array<DateTime> = []
) => {
  let date = dateInput;
  while (
    date.weekday > 5 ||
    holidays.some((holiday) => holiday.hasSame(date, 'day'))
  ) {
    date = date.minus({ days: 1 });
  }
  return date;
};

/**
 * Calculates the processing cutoff date for a pay-period end date. Use in
 * provider dashboard pay-period calculations.
 */
export const getCutoffDate = (
  payEndDate: DateTime,
  holidays?: Array<DateTime>,
  province = 'MB'
) =>
  rollBackToBusinessDay(
    payEndDate.minus({ days: 8 }),
    holidays ?? getCutoffHolidays(payEndDate, province)
  );

/**
 * Finds the cutoff interval containing `dateInput`. Use when grouping provider
 * dashboard data into the active processing window.
 */
export const getCutoffRange = (
  dateInput: DateTime,
  holidays?: DateTime[],
  province = 'MB'
) => {
  const months = [
    dateInput.minus({ months: 1 }),
    dateInput,
    dateInput.plus({ months: 1 }),
  ];

  const cutoffs = months
    .flatMap((month) => [
      month.set({ day: 15 }).startOf('day'),
      month.endOf('month'),
    ])
    .map((payEnd) => getCutoffDate(payEnd, holidays, province))
    .sort((left, right) => left.toMillis() - right.toMillis());

  for (let index = 0; index < cutoffs.length - 1; index++) {
    if (dateInput >= cutoffs[index] && dateInput < cutoffs[index + 1]) {
      return {
        start: cutoffs[index],
        end: cutoffs[index + 1],
      };
    }
  }

  /* c8 ignore start */
  throw new Error('Date is outside the generated cutoff windows.');
  /* c8 ignore end */
};
